import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Sparkles, Crown, BarChart3, Tag, Menu, X, LogOut, User as UserIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import CurrencyPicker from "./CurrencyPicker";

const navItems = [
  { to: "/compare", label: "Compare", icon: BarChart3 },
  { to: "/ai-finder", label: "AI Name Finder", icon: Sparkles },
  { to: "/premium-domains", label: "Premium Domains", icon: Crown },
  { to: "/pricing", label: "Pricing", icon: Tag },
  { to: "/blog", label: "Blog", icon: Search },
];

export default function TopNav() {
  const { user, requestLogin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-zinc-200/80 bg-white/80 backdrop-blur-xl" data-testid="top-nav">
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="/logo.png" alt="Dompryce" className="h-9 w-9 rounded-lg" />
          <span className="font-display text-xl font-bold tracking-tight text-zinc-900">Dompryce</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="primary-nav">
          {navItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-zinc-100 text-zinc-900" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <CurrencyPicker />
          {user ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard")}
                data-testid="dashboard-btn"
                className="gap-2"
              >
                <UserIcon className="h-4 w-4" />
                {user.name?.split(" ")[0] || "Dashboard"}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="gap-2">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => requestLogin("/dashboard")} data-testid="signin-btn">
                Sign in
              </Button>
              <Button
                size="sm"
                onClick={() => requestLogin("/dashboard")}
                data-testid="signup-btn"
                className="bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Get started
              </Button>
            </>
          )}
        </div>

        <button
          className="rounded-lg p-2 text-zinc-700 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-zinc-200 bg-white lg:hidden" data-testid="mobile-nav">
          <div className="space-y-1 px-4 py-4">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-zinc-100 text-zinc-900" : "text-zinc-700"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            <div className="mt-3 flex gap-2 border-t border-zinc-100 pt-3">
              {user ? (
                <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="flex-1">Dashboard</Button>
              ) : (
                <Button onClick={() => requestLogin("/dashboard")} className="flex-1 bg-indigo-600 text-white hover:bg-indigo-500">
                  Sign in
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
